// src/pages/PgPreviewPage.js
// Read-only view of the PostgreSQL inventory — used to verify migration data.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import "../App.css";

const OWNERSHIP_LABELS = {
  sna_stock:     "SNA Stock",
  customer_unit: "Customer Unit",
  demo:          "Demo",
  rental:        "Rental",
};

const SHEETS = ["allInventory", "usedStock", "unknownUnits", "snaAssets", "2026Sold"];

function StatCard({ label, value, sub }) {
  return (
    <div className="card" style={{ minWidth: 140, flex: 1 }}>
      <div style={{ fontWeight: 900, fontSize: "1.6rem" }}>{value}</div>
      <div style={{ fontWeight: 700, marginTop: 2 }}>{label}</div>
      {sub && <div className="muted" style={{ fontSize: "0.78rem", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function PgPreviewPage() {
  const nav = useNavigate();

  // Stats
  const [stats, setStats]       = useState(null);
  const [statsErr, setStatsErr] = useState("");

  // Units list
  const [units, setUnits]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState("");

  // Filters
  const [q, setQ]                   = useState("");
  const [colorCode, setColorCode]   = useState("");
  const [ownership, setOwnership]   = useState("");
  const [sourceSheet, setSourceSheet] = useState("");
  const [offset, setOffset]         = useState(0);
  const LIMIT = 100;

  // Detail drawer
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get("/pg/stats")
      .then(({ data }) => setStats(data))
      .catch(e => setStatsErr(e?.response?.data?.error || e.message));
  }, []);

  const load = useCallback(async (off = 0) => {
    setLoading(true); setErr("");
    try {
      const params = { limit: LIMIT, offset: off };
      if (q)           params.q            = q;
      if (colorCode)   params.color_code   = colorCode;
      if (ownership)   params.ownership    = ownership;
      if (sourceSheet) params.source_sheet = sourceSheet;
      const { data } = await api.get("/pg/units", { params });
      setUnits(data.units || []);
      setTotal(data.total || 0);
      setOffset(off);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [q, colorCode, ownership, sourceSheet]);

  useEffect(() => { load(0); }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true); setSelected(null);
    try {
      const { data } = await api.get(`/pg/units/${id}`);
      setSelected(data);
    } catch (e) { alert(e?.response?.data?.error || e.message); }
    finally { setDetailLoading(false); }
  };

  const pages = Math.ceil(total / LIMIT);
  const page  = Math.floor(offset / LIMIT);

  return (
    <div className="container">

      {/* Header */}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>PostgreSQL Preview</h2>
          <div className="muted" style={{ marginTop: 3 }}>
            Read-only view — verify migration data before cutover
          </div>
        </div>
        <button className="btn-outline" onClick={() => nav(-1)}>← Back</button>
      </div>

      {/* Stats */}
      {statsErr && <div className="muted" style={{ marginTop: 10, color: "var(--danger,#e05)" }}>{statsErr}</div>}
      {stats && (
        <div className="row" style={{ gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <StatCard label="Total Units"      value={stats.total_units} />
          <StatCard label="Total Components" value={stats.total_components}
            sub={`avg ${(stats.total_components / stats.total_units).toFixed(1)}/unit`} />
          {stats.by_ownership.map(o => (
            <StatCard key={o.ownership}
              label={OWNERSHIP_LABELS[o.ownership] || o.ownership}
              value={parseInt(o.n, 10)} />
          ))}
        </div>
      )}

      {/* By sheet */}
      {stats && (
        <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {stats.by_sheet.map(s => (
            <div key={s.source_sheet} className="pill" style={{ cursor: "pointer" }}
              onClick={() => { setSourceSheet(s.source_sheet === sourceSheet ? "" : s.source_sheet); }}>
              {s.source_sheet}: {s.n}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <input className="input" placeholder="Search stock#, RO#, customer…"
            style={{ flex: 2, minWidth: 200 }}
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load(0)} />

          <select className="input" style={{ flex: 1, minWidth: 140 }}
            value={colorCode} onChange={e => setColorCode(e.target.value)}>
            <option value="">All statuses</option>
            {stats?.by_status.map(s => (
              <option key={s.color_code} value={s.color_code}>
                {s.status_label} ({s.count})
              </option>
            ))}
          </select>

          <select className="input" style={{ flex: 1, minWidth: 140 }}
            value={ownership} onChange={e => setOwnership(e.target.value)}>
            <option value="">All ownership</option>
            <option value="sna_stock">SNA Stock</option>
            <option value="customer_unit">Customer Unit</option>
            <option value="demo">Demo</option>
            <option value="rental">Rental</option>
          </select>

          <select className="input" style={{ flex: 1, minWidth: 140 }}
            value={sourceSheet} onChange={e => setSourceSheet(e.target.value)}>
            <option value="">All sheets</option>
            {SHEETS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <button className="btn" onClick={() => load(0)} disabled={loading}>Search</button>
          <button className="btn-outline" onClick={() => {
            setQ(""); setColorCode(""); setOwnership(""); setSourceSheet(""); load(0);
          }}>Clear</button>
        </div>
      </div>

      {/* Error */}
      {err && <div className="muted" style={{ marginTop: 8, color: "var(--danger,#e05)" }}>{err}</div>}

      {/* Pagination */}
      <div className="row" style={{ marginTop: 10, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="muted" style={{ fontSize: "0.85rem" }}>
          {loading ? "Loading…" : `${total.toLocaleString()} units · page ${page + 1} of ${pages || 1}`}
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn-outline" disabled={offset === 0 || loading}
            onClick={() => load(Math.max(0, offset - LIMIT))}>← Prev</button>
          <button className="btn-outline" disabled={offset + LIMIT >= total || loading}
            onClick={() => load(offset + LIMIT)}>Next →</button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--card-border)" }}>
              {["ID","Stock PO","RO/WO","Customer","Status","Ownership","Sheet","Components","Location","Notes?"].map(h => (
                <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {units.map((u, i) => (
              <tr key={u.id}
                style={{
                  borderBottom: "1px solid var(--card-border)",
                  background: i % 2 === 0 ? "transparent" : "var(--card-bg,rgba(255,255,255,0.02))",
                  cursor: "pointer",
                }}
                onClick={() => openDetail(u.id)}
              >
                <td style={{ padding: "5px 10px", color: "var(--muted)" }}>{u.id}</td>
                <td style={{ padding: "5px 10px", fontWeight: 700 }}>{u.stock_po || <span className="muted">—</span>}</td>
                <td style={{ padding: "5px 10px" }}>{u.rowo || <span className="muted">—</span>}</td>
                <td style={{ padding: "5px 10px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={u.customer}>{u.customer || <span className="muted">—</span>}</td>
                <td style={{ padding: "5px 10px" }}>
                  <span className="pill" style={{ fontSize: "0.75rem" }}>{u.status_label}</span>
                </td>
                <td style={{ padding: "5px 10px", color: "var(--muted)", fontSize: "0.8rem" }}>
                  {OWNERSHIP_LABELS[u.ownership] || u.ownership}
                </td>
                <td style={{ padding: "5px 10px", color: "var(--muted)", fontSize: "0.8rem" }}>{u.source_sheet}</td>
                <td style={{ padding: "5px 10px", textAlign: "center" }}>
                  {(u.components || []).map(c => (
                    <span key={c.id} className="pill" style={{ fontSize: "0.7rem", marginRight: 3 }}>{c.type}</span>
                  ))}
                </td>
                <td style={{ padding: "5px 10px", color: "var(--muted)", fontSize: "0.8rem" }}>{u.unit_location || "—"}</td>
                <td style={{ padding: "5px 10px", textAlign: "center" }}>
                  {(u.notes || u.notes2) ? "✓" : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && units.length === 0 && (
          <div className="muted" style={{ padding: "20px 10px", textAlign: "center" }}>No units found.</div>
        )}
      </div>

      {/* Detail drawer */}
      {(selected || detailLoading) && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", justifyContent: "flex-end", zIndex: 500,
        }} onClick={() => setSelected(null)}>
          <div style={{
            width: "min(600px, 100vw)", height: "100%", overflowY: "auto",
            background: "var(--bg)", borderLeft: "1px solid var(--card-border)",
            padding: 24,
          }} onClick={e => e.stopPropagation()}>
            <button className="btn-outline" style={{ marginBottom: 16 }} onClick={() => setSelected(null)}>✕ Close</button>
            {detailLoading && <div className="muted">Loading…</div>}
            {selected && <>
              <div style={{ fontWeight: 900, fontSize: "1.2rem", marginBottom: 4 }}>
                {selected.stock_po || selected.rowo || `Unit #${selected.id}`}
              </div>
              <div className="muted" style={{ marginBottom: 16 }}>{selected.status_label} · {OWNERSHIP_LABELS[selected.ownership]}</div>

              <Section label="Identity">
                <Row k="PG ID"        v={selected.id} />
                <Row k="Stock PO"     v={selected.stock_po} />
                <Row k="RO/WO"        v={selected.rowo} />
                <Row k="Customer PO"  v={selected.customer_po} />
                <Row k="SNA Cust PO"  v={selected.sna_customer_po} />
                <Row k="Customer"     v={selected.customer} />
                <Row k="Location"     v={selected.unit_location} />
                <Row k="Source Sheet" v={selected.source_sheet} />
                <Row k="Import Batch" v={selected.import_batch} />
              </Section>

              <Section label="Dates">
                <Row k="Cust PO Date"      v={selected.customer_po_date} />
                <Row k="Factory Order"     v={selected.factory_order_date} />
                <Row k="Ready at Factory"  v={selected.ready_at_factory} />
                <Row k="Arrive SNA"        v={selected.arrive_sna} />
                <Row k="To Customer"       v={selected.to_customer} />
              </Section>

              <Section label="Financials">
                <Row k="Body Net SNA"    v={fmt(selected.body_net_sna)} />
                <Row k="Chassis Net SNA" v={fmt(selected.chassis_net_sna)} />
                <Row k="Total Net SNA"   v={fmt(selected.total_net_sna)} />
                <Row k="Total Sell"      v={fmt(selected.total_sell)} />
                <Row k="Freight"         v={fmt(selected.freight_charges)} />
                <Row k="PDI"             v={fmt(selected.pdi_charges)} />
                <Row k="Surcharge"       v={fmt(selected.surcharge)} />
                <Row k="Tariff"          v={fmt(selected.tariff)} />
                <Row k="Exchange"        v={fmt(selected.exchange)} />
              </Section>

              {(selected.components || []).length > 0 && (
                <Section label={`Components (${selected.components.length})`}>
                  {selected.components.map((c, i) => (
                    <div key={i} style={{ marginBottom: 10, paddingLeft: 8, borderLeft: "2px solid var(--card-border)" }}>
                      <div style={{ fontWeight: 700 }}>{c.type?.toUpperCase()} {c.suffix ? `(${c.suffix})` : ""}</div>
                      <Row k="Make/Model"  v={c.make_model} />
                      <Row k="Body VIN"    v={c.body_vin} />
                      <Row k="Chassis VIN" v={c.chassis_vin} />
                      <Row k="Factory WO"  v={c.factory_wo} />
                      <Row k="Factory SO"  v={c.factory_prod_so} />
                      <Row k="Net Cost"    v={fmt(c.net_cost)} />
                    </div>
                  ))}
                </Section>
              )}

              {(selected.notes || selected.notes2) && (
                <Section label="Notes">
                  {selected.notes  && <div style={{ marginBottom: 6, whiteSpace: "pre-wrap" }}>{selected.notes}</div>}
                  {selected.notes2 && <div style={{ whiteSpace: "pre-wrap", color: "var(--muted)" }}>{selected.notes2}</div>}
                </Section>
              )}
            </>}
          </div>
        </div>
      )}
    </div>
  );
}

function fmt(v) {
  if (v == null) return null;
  return "$" + Number(v).toLocaleString("en-CA", { minimumFractionDigits: 2 });
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, borderBottom: "1px solid var(--card-border)", paddingBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  if (v == null || v === "") return null;
  return (
    <div className="row" style={{ gap: 8, marginBottom: 3, fontSize: "0.85rem" }}>
      <span className="muted" style={{ minWidth: 130 }}>{k}</span>
      <span>{String(v)}</span>
    </div>
  );
}
